import { CORPUS_START } from './generator';
import { REFERENCE_DATE } from './reference';

/**
 * The window a DeepDive AI question asks about.
 *
 * The user's call (2026-09-19): **all time unless the question names a
 * period**. The chat screens draw no date pickers, so the dashboard's range is
 * invisible there and using it would make an answer depend on a control the
 * user cannot see. A question can still narrow itself — "last month", "since
 * July", "in Q2", "the past 90 days" — and the answer names whichever window it
 * used, so the reader never has to guess.
 *
 * Every bound is a local-midnight day number, the corpus's own unit, and the
 * window is clipped to the corpus: "in 2024" asks for days the generator never
 * wrote, and the honest answer to that is "no conversations", not an error.
 */
export type Period = {
  /** Local-midnight ms, inclusive. */
  from: number;
  to: number;
  /** For a heading: `the last 30 days (17 Aug – 15 Sep 2026)`. */
  label: string;
  /** For a sentence: `over the last 30 days (17 Aug – 15 Sep 2026)`. */
  phrase: string;
  /** Whether the question named a period at all. */
  named: boolean;
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
/** Monday, per the calendar the date picker draws. */
const startOfWeek = (d: Date) => addDays(startOfDay(d), -((d.getDay() + 6) % 7));

const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const;
const MONTH_PATTERN = MONTHS.map((m) => `${m.slice(0, 3)}(?:${m.slice(3)})?`).join('|');

const WORD_NUMBERS: Readonly<Record<string, number>> = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
  eight: 8, nine: 9, ten: 10, twelve: 12, fourteen: 14, thirty: 30, sixty: 60, ninety: 90,
};

/*
 * Hand-rolled rather than `Intl`, because en-GB abbreviates September as
 * "Sept" and every axis in the app says "Sep" (see `bucketLabel`), and en-US
 * puts the month first where the date picker and the prototype's own copy put
 * the day first.
 */
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const dayOf = (d: Date) => `${d.getDate()} ${MON[d.getMonth()]}`;
const dayYear = (d: Date) => `${dayOf(d)} ${d.getFullYear()}`;
const monthYear = (d: Date) => `${MONTHS[d.getMonth()][0].toUpperCase()}${MONTHS[d.getMonth()].slice(1)} ${d.getFullYear()}`;

/** `17 Aug – 15 Sep 2026`, `12 Aug 2025 – 15 Sep 2026` across a year end, or one day's date. */
function span(from: number, to: number): string {
  const a = new Date(from);
  const b = new Date(to);
  if (from === to) return dayYear(a);
  const sameYear = a.getFullYear() === b.getFullYear();
  return `${sameYear ? dayOf(a) : dayYear(a)} – ${dayYear(b)}`;
}

function monthIndex(name: string): number {
  return MONTHS.findIndex((m) => m.startsWith(name.slice(0, 3)));
}

export function parsePeriod(question: string, reference: Date = REFERENCE_DATE): Period {
  const q = question.toLowerCase();
  const today = startOfDay(reference);
  const floor = startOfDay(CORPUS_START);

  /*
   * Clip to the corpus and finish the two strings. A window that ends before
   * it starts is left that way — it reads as "no conversations", which is what
   * asking about next December deserves.
   */
  const build = (from: Date, to: Date, label: string, preposition: string, named = true): Period => {
    const f = Math.max(from.getTime(), floor.getTime());
    const t = Math.min(to.getTime(), today.getTime());
    /* A window the corpus never reaches has no dates worth printing beside its name. */
    const full = f <= t ? `${label} (${span(f, t)})` : label;
    return {
      from: f,
      to: t,
      label: full,
      phrase: preposition === '' ? full : `${preposition} ${full}`,
      named,
    };
  };

  const allTime = (named: boolean) => build(floor, today, 'all time', 'across', named);

  if (/\ball[- ]time\b|\boverall\b|\bever\b|\bentire\b|\bwhole (?:period|history)\b/.test(q)) {
    return allTime(true);
  }

  /* "the last 30 days", "past two weeks", "previous 3 months", "trailing year". */
  let m = q.match(
    /\b(?:last|past|previous|trailing)\s+(\d+|a|an|one|two|three|four|five|six|seven|eight|nine|ten|twelve|fourteen|thirty|sixty|ninety)\s+(day|week|month|year)s?\b/,
  );
  if (m) {
    const n = WORD_NUMBERS[m[1]] ?? Number(m[1]);
    const unit = m[2] as 'day' | 'week' | 'month' | 'year';
    let from: Date;
    if (unit === 'day') from = addDays(today, -(n - 1));
    else if (unit === 'week') from = addDays(today, -(n * 7 - 1));
    else if (unit === 'month') from = addDays(new Date(today.getFullYear(), today.getMonth() - n, today.getDate()), 1);
    else from = addDays(new Date(today.getFullYear() - n, today.getMonth(), today.getDate()), 1);
    return build(from, today, `the last ${n} ${unit}${n === 1 ? '' : 's'}`, 'over');
  }

  /* "last week" is the previous calendar week; "this week" is Monday to today. */
  m = q.match(/\b(last|past|previous|this)\s+(week|month|quarter|year)\b/);
  if (m) {
    const current = m[1] === 'this';
    const unit = m[2];
    if (unit === 'week') {
      const start = startOfWeek(today);
      return current
        ? build(start, today, 'this week', 'so far')
        : build(addDays(start, -7), addDays(start, -1), 'last week', 'over');
    }
    if (unit === 'month') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return current
        ? build(start, today, 'this month', 'so far')
        : build(new Date(today.getFullYear(), today.getMonth() - 1, 1), addDays(start, -1), 'last month', 'over');
    }
    if (unit === 'quarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      const start = new Date(today.getFullYear(), quarter * 3, 1);
      return current
        ? build(start, today, 'this quarter', 'so far')
        : build(new Date(today.getFullYear(), quarter * 3 - 3, 1), addDays(start, -1), 'last quarter', 'over');
    }
    const start = new Date(today.getFullYear(), 0, 1);
    return current
      ? build(start, today, 'this year', 'so far')
      : build(new Date(today.getFullYear() - 1, 0, 1), addDays(start, -1), 'last year', 'over');
  }

  if (/\byesterday\b/.test(q)) {
    const y = addDays(today, -1);
    return build(y, y, 'yesterday', '');
  }
  if (/\btoday\b/.test(q)) return build(today, today, 'today', '');

  /* "Q2", "Q3 2026". */
  m = q.match(/\bq([1-4])(?:\s+(20\d{2}))?\b/);
  if (m) {
    const quarter = Number(m[1]) - 1;
    let year = m[2] ? Number(m[2]) : today.getFullYear();
    if (!m[2] && quarter * 3 > today.getMonth()) year -= 1;
    const start = new Date(year, quarter * 3, 1);
    return build(start, new Date(year, quarter * 3 + 3, 0), `Q${quarter + 1} ${year}`, 'in');
  }

  /* "since July" runs to today; "in August", "during Aug 2026" is the month. */
  m = q.match(new RegExp(`\\b(since|from)\\s+(${MONTH_PATTERN})(?:\\s+(20\\d{2}))?\\b`));
  if (m) {
    const month = monthIndex(m[2]);
    let year = m[3] ? Number(m[3]) : today.getFullYear();
    if (!m[3] && month > today.getMonth()) year -= 1;
    const start = new Date(year, month, 1);
    return build(start, today, `since ${monthYear(start)}`, '');
  }
  m = q.match(new RegExp(`\\b(${MONTH_PATTERN})(?:\\s+(20\\d{2}))?\\b`));
  if (m) {
    const month = monthIndex(m[1]);
    let year = m[2] ? Number(m[2]) : today.getFullYear();
    if (!m[2] && month > today.getMonth()) year -= 1;
    const start = new Date(year, month, 1);
    return build(start, new Date(year, month + 1, 0), monthYear(start), 'in');
  }

  /* A bare year. */
  m = q.match(/\b(20\d{2})\b/);
  if (m) {
    const year = Number(m[1]);
    return build(new Date(year, 0, 1), new Date(year, 11, 31), String(year), 'in');
  }

  return allTime(false);
}
